import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader, DetailList, DetailRow } from '../components/ui/Card';
import { AsyncBoundary, EmptyState, Skeleton, SkeletonRows } from '../components/ui/AsyncBoundary';
import { Field, Input, Select } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Icon } from '../components/ui/Icon';
import { Modal, useToast } from '../components/ui/Overlay';
import { Can } from '../auth/Can';
import { useAuth } from '../auth/AuthProvider';
import { useResource, useMutation } from '../hooks/useResource';
import { api } from '../lib/api';
import * as fmt from '../lib/format';

const ROLLOUT_TONE = {
  DRAFT: 'neutral', RUNNING: 'brand', PAUSED: 'warning',
  HALTED: 'critical', COMPLETED: 'good', CANCELLED: 'neutral',
};
const TARGET_TONE = {
  PENDING: 'neutral', SENT: 'brand', UPDATING: 'brand',
  SUCCEEDED: 'good', FAILED: 'critical', SKIPPED: 'warning', CANCELLED: 'neutral',
};

/**
 * Firmware: the catalogue of images, and the rollouts carrying them.
 *
 * Kept as two lists on one page because they are read together — "what is the
 * newest build, and is anyone running it yet" is one question, and splitting
 * it across two screens makes an operator hold the answer in their head.
 */
export function Firmware() {
  const [creating, setCreating] = useState(false);
  const [rollingOut, setRollingOut] = useState(null);

  const releases = useResource('/api/firmware/releases');
  const rollouts = useResource('/api/firmware/rollouts', null, { pollMs: 30_000 });
  const orgs = useResource('/api/orgs');

  return (
    <Page className="space-y-4">
      <PageHeader
        title="Firmware"
        subtitle="Images available, and the rollouts carrying them"
        actions={
          <Can do="device:ota">
            <Button variant="accent" size="sm" icon="plus" onClick={() => setCreating(true)}>
              Add release
            </Button>
          </Can>
        }
      />

      <Card className="overflow-hidden">
        <CardHeader
          title="Releases"
          subtitle="The platform stores the location and the digest; the device fetches and verifies"
          icon="chip"
        />
        <AsyncBoundary
          state={releases}
          skeleton={<SkeletonRows rows={4} />}
          isEmpty={() => !releases.data?.releases?.length}
          empty={
            <EmptyState
              icon="chip"
              title="No firmware registered"
              description="Add a release to make an image available for rollout."
            />
          }
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.releases.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-[13.5px] font-medium text-ink">
                      <span className="font-mono">{r.version}</span>
                      <span className="text-ink-3">{r.hardware_model}</span>
                      {r.published ? (
                        <Badge tone="good" size="sm" icon={null}>Published</Badge>
                      ) : (
                        <Badge tone="neutral" size="sm" icon={null}>Draft</Badge>
                      )}
                    </p>
                    <p className="truncate text-[12px] text-ink-3">
                      {r.org_name ? `${r.org_name} only` : 'All organisations'}
                      {' · '}
                      {r.release_notes || 'No notes'}
                    </p>
                  </div>
                  <Can do="device:ota">
                    <div className="flex items-center gap-2">
                      <PublishButton release={r} onDone={releases.refetch} />
                      <Button
                        variant="secondary"
                        size="sm"
                        icon="arrow"
                        disabled={!r.published}
                        onClick={() => setRollingOut(r)}
                      >
                        Roll out
                      </Button>
                    </div>
                  </Can>
                </li>
              ))}
            </ul>
          )}
        </AsyncBoundary>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader title="Rollouts" subtitle="One organisation each, paced independently" icon="history" />
        <AsyncBoundary
          state={rollouts}
          skeleton={<SkeletonRows rows={3} />}
          isEmpty={() => !rollouts.data?.rollouts?.length}
          empty={
            <EmptyState
              icon="history"
              title="Nothing has been rolled out"
              description="Publish a release, then roll it out to an organisation."
            />
          }
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.rollouts.map((r) => {
                const total = Number(r.total) || 0;
                const done = Number(r.succeeded) || 0;
                return (
                  <li key={r.id}>
                    <Link
                      to={`/firmware/rollouts/${r.id}`}
                      className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-surface-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-[13.5px] font-medium text-ink">
                          <span className="font-mono">{r.version}</span>
                          <span className="text-ink-3">→ {r.org_name}</span>
                          <Badge tone={ROLLOUT_TONE[r.state] || 'neutral'} size="sm" icon={null}>
                            {r.state.toLowerCase()}
                          </Badge>
                        </p>
                        <p className="text-[12px] text-ink-3">
                          wave {r.current_wave} · {done} of {total} on the new image
                          {Number(r.failed) ? ` · ${r.failed} failed` : ''}
                        </p>
                      </div>
                      <Icon name="chevronRight" size="sm" className="text-ink-3" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </AsyncBoundary>
      </Card>

      <NewReleaseModal
        open={creating}
        onClose={() => setCreating(false)}
        organizations={orgs.data?.organizations || []}
        onCreated={releases.refetch}
      />
      <StartRolloutModal
        release={rollingOut}
        onClose={() => setRollingOut(null)}
        organizations={orgs.data?.organizations || []}
        onStarted={() => { releases.refetch(); rollouts.refetch(); }}
      />
    </Page>
  );
}

function PublishButton({ release, onDone }) {
  const toast = useToast();
  const mutate = useMutation(async () =>
    api.post(`/api/firmware/releases/${release.id}/publish`, { published: !release.published })
  );
  const run = async () => {
    try {
      await mutate.run();
      onDone();
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <Button variant="ghost" size="sm" loading={mutate.pending} onClick={run}>
      {release.published ? 'Unpublish' : 'Publish'}
    </Button>
  );
}

/**
 * Registering an image.
 *
 * The digest is not optional and not a formality: it is the only thing
 * standing between a truncated download and a device that flashes it anyway.
 * The device verifies it before writing, so a wrong value here means the
 * update fails safely rather than bricking a unit.
 */
function NewReleaseModal({ open, onClose, organizations, onCreated }) {
  const toast = useToast();
  const { user } = useAuth();
  const empty = {
    version: '', hardware_model: '', url: '', sha256: '',
    size_bytes: '', release_notes: '', min_version: '', org_id: '',
  };
  const [form, setForm] = useState(empty);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const create = useMutation(async () =>
    api.post('/api/firmware/releases', {
      version: form.version.trim(),
      hardware_model: form.hardware_model.trim(),
      url: form.url.trim(),
      sha256: form.sha256.trim().toLowerCase(),
      size_bytes: form.size_bytes ? Number(form.size_bytes) : undefined,
      release_notes: form.release_notes || undefined,
      min_version: form.min_version || undefined,
      org_id: form.org_id || undefined,
    })
  );

  const run = async () => {
    try {
      await create.run();
      toast.success('Release added as a draft. Publish it to make it available.');
      setForm(empty);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add firmware release"
      description="The image stays where you host it; the platform records where and what."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.pending} onClick={run}>Add release</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Version" hint="MAJOR.MINOR.PATCH — 1.4.2">
            <Input value={form.version} onChange={set('version')} placeholder="1.5.0" />
          </Field>
          <Field label="Hardware model" hint="Must match what devices report">
            <Input value={form.hardware_model} onChange={set('hardware_model')} placeholder="TCU-4G-V2" />
          </Field>
        </div>

        <Field label="Image URL" hint="https only; the device downloads this directly">
          <Input value={form.url} onChange={set('url')} placeholder="https://storage.example.com/fw/1.5.0.bin" />
        </Field>

        <Field label="SHA-256" hint="64 hex characters. The device verifies this before flashing.">
          <Input value={form.sha256} onChange={set('sha256')} className="font-mono text-[12px]" />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Size in bytes" hint="Optional — lets the device check free space first">
            <Input value={form.size_bytes} onChange={set('size_bytes')} inputMode="numeric" />
          </Field>
          <Field label="Minimum version" hint="Optional — skip devices older than this">
            <Input value={form.min_version} onChange={set('min_version')} placeholder="1.2.0" />
          </Field>
        </div>

        {user?.role === 'SUPER_ADMIN' && (
          <Field label="Organisation" hint="Leave empty to make this available to every organisation">
            <Select value={form.org_id} onChange={set('org_id')}>
              <option value="">All organisations</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Release notes">
          <Input value={form.release_notes} onChange={set('release_notes')} placeholder="What changed" />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Starting a rollout.
 *
 * The preview is the important part of this dialog. It shows exactly which
 * devices are in wave 1 before anything is sent, because "roll out to the
 * org" is an abstraction and "this lorry, in ten seconds" is what actually
 * happens.
 */
function StartRolloutModal({ release, onClose, organizations, onStarted }) {
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [waveSize, setWaveSize] = useState(1);
  const [waveGrowth, setWaveGrowth] = useState(5);
  const [threshold, setThreshold] = useState(20);
  const [autoAdvance, setAutoAdvance] = useState(false);

  const effectiveOrg = user?.role === 'SUPER_ADMIN' ? orgId : user?.org_id;

  const preview = useResource(
    release && effectiveOrg ? `/api/firmware/releases/${release.id}/preview` : null,
    { org_id: effectiveOrg, wave_size: waveSize, wave_growth: waveGrowth }
  );

  const create = useMutation(async () =>
    api.post('/api/firmware/rollouts', {
      release_id: release.id,
      org_id: effectiveOrg,
      wave_size: Number(waveSize),
      wave_growth: Number(waveGrowth),
      failure_threshold_pct: Number(threshold),
      auto_advance: autoAdvance,
    })
  );

  const run = async () => {
    try {
      const result = await create.run();
      toast.success(`Rollout created: ${result.targetCount} devices in ${result.waveCount} waves.`);
      onStarted();
      onClose();
      navigate(`/firmware/rollouts/${result.rollout.id}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!release) return null;
  const waves = preview.data?.waves || [];

  return (
    <Modal
      open={Boolean(release)}
      onClose={onClose}
      title={`Roll out ${release.version}`}
      description={`${release.hardware_model} · created as a draft; nothing is sent until you start wave 1.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.pending}
            disabled={!effectiveOrg || !preview.data?.eligible}
            onClick={run}
          >
            Create rollout
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {user?.role === 'SUPER_ADMIN' && (
          <Field label="Organisation" hint="A rollout belongs to one organisation">
            <Select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">Choose…</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Canary size" hint="Devices in wave 1">
            <Input value={waveSize} onChange={(e) => setWaveSize(e.target.value)} inputMode="numeric" />
          </Field>
          <Field label="Wave growth" hint="Each wave × this">
            <Input value={waveGrowth} onChange={(e) => setWaveGrowth(e.target.value)} inputMode="numeric" />
          </Field>
          <Field label="Halt above" hint="% failures in a wave">
            <Input value={threshold} onChange={(e) => setThreshold(e.target.value)} inputMode="numeric" />
          </Field>
        </div>

        <label className="flex items-start gap-2 rounded-lg border border-line px-3 py-2.5 text-[13px]">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={(e) => setAutoAdvance(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-ink">Advance automatically</span>
            <span className="block text-ink-3">
              Off by default. A person looking at the canary before the fleet gets the image is the
              reason to stage a rollout at all.
            </span>
          </span>
        </label>

        {effectiveOrg && (
          <AsyncBoundary state={preview} skeleton={<Skeleton className="h-24 w-full" />}>
            {(data) =>
              data.eligible ? (
                <div className="rounded-lg border border-line">
                  <p className="border-b border-line px-3 py-2 text-[12.5px] text-ink-2">
                    {data.eligible} eligible device{data.eligible === 1 ? '' : 's'} in {waves.length} wave
                    {waves.length === 1 ? '' : 's'}
                  </p>
                  <ul className="max-h-40 divide-y divide-line overflow-y-auto">
                    {waves.map((w) => (
                      <li key={w.wave} className="px-3 py-2 text-[12.5px]">
                        <span className="font-medium text-ink">Wave {w.wave}</span>
                        <span className="text-ink-3"> · {w.devices.length} device{w.devices.length === 1 ? '' : 's'}</span>
                        {w.wave === 1 && (
                          <span className="block font-mono text-[11.5px] text-ink-3">
                            {w.devices.map((d) => d.reg_number || d.imei).join(', ')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2.5 text-[13px] text-warning-ink">
                  Nothing in this organisation is a {release.hardware_model} that is not already on{' '}
                  {release.version}.
                </p>
              )
            }
          </AsyncBoundary>
        )}
      </div>
    </Modal>
  );
}

/** One rollout: where each wave got to, and the controls to move it. */
export function RolloutDetail() {
  const { id } = useParams();
  const toast = useToast();
  const data = useResource(`/api/firmware/rollouts/${id}`, null, { pollMs: 20_000 });

  const act = useMutation(async (action, body = {}) =>
    api.post(`/api/firmware/rollouts/${id}/${action}`, body)
  );

  const run = async (action, body) => {
    try {
      const result = await act.run(action, body);
      toast.success(result.note || `Rollout ${action}d.`);
      data.refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Page className="space-y-4">
      <AsyncBoundary state={data} skeleton={<Skeleton className="h-96 w-full" />}>
        {({ rollout: r, targets, currentWave }) => {
          const byWave = targets.reduce((acc, t) => {
            (acc[t.wave] = acc[t.wave] || []).push(t);
            return acc;
          }, {});
          const live = ['DRAFT', 'RUNNING', 'PAUSED'].includes(r.state);

          return (
            <>
              <PageHeader
                breadcrumb={
                  <Link to="/firmware" className="mb-1 inline-flex items-center gap-1 text-[12.5px] text-ink-3 hover:text-ink-2">
                    <Icon name="chevronLeft" size="xs" />
                    Firmware
                  </Link>
                }
                title={`${r.version} → ${r.org_name}`}
                subtitle={r.hardware_model}
                actions={
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={ROLLOUT_TONE[r.state] || 'neutral'} size="sm" icon={null}>
                      {r.state.toLowerCase()}
                    </Badge>
                    <Can do="device:ota">
                      {live && (
                        <Button variant="accent" size="sm" icon="arrow" loading={act.pending}
                          onClick={() => run('advance')}>
                          {r.current_wave === 0 ? 'Start canary' : `Approve wave ${r.current_wave + 1}`}
                        </Button>
                      )}
                      {r.state === 'RUNNING' && (
                        <Button variant="secondary" size="sm" icon="pause" onClick={() => run('pause')}>
                          Pause
                        </Button>
                      )}
                      {live && (
                        <Button variant="danger" size="sm" icon="square" onClick={() => run('halt')}>
                          Halt
                        </Button>
                      )}
                    </Can>
                  </div>
                }
              />

              {r.halted_reason && (
                <div className="flex items-start gap-2 rounded-lg border border-critical/30 bg-critical-soft px-3 py-2.5 text-[13px] text-critical-ink">
                  <Icon name="critical" size="sm" className="mt-0.5" />
                  <p>Halted — {r.halted_reason}</p>
                </div>
              )}

              <div className="grid gap-4 xl:grid-cols-[1fr_1.6fr]">
                <Card>
                  <CardHeader title="Rollout" icon="chip" />
                  <div className="px-4 py-2">
                    <DetailList>
                      <DetailRow label="Version" value={r.version} mono />
                      <DetailRow label="Hardware" value={r.hardware_model} />
                      <DetailRow label="Canary size" value={r.wave_size} />
                      <DetailRow label="Wave growth" value={`×${r.wave_growth}`} />
                      <DetailRow label="Halt above" value={`${r.failure_threshold_pct}% failures`} />
                      <DetailRow label="Advance" value={r.auto_advance ? 'Automatic' : 'Manual approval'} />
                      <DetailRow label="Current wave" value={r.current_wave || 'not started'} />
                      <DetailRow label="Created" value={fmt.dateTime(r.created_at)} />
                    </DetailList>
                  </div>
                  {currentWave?.total ? (
                    <p className="border-t border-line px-4 py-2.5 text-[12.5px] text-ink-3">
                      Wave {r.current_wave}: {currentWave.succeeded} succeeded, {currentWave.failed} failed,{' '}
                      {currentWave.inFlight} in flight.
                    </p>
                  ) : null}
                </Card>

                <Card className="overflow-hidden">
                  <CardHeader title="Devices" subtitle="By wave" icon="truck" />
                  <div className="max-h-[32rem] overflow-y-auto">
                    {Object.keys(byWave).sort((a, b) => a - b).map((wave) => (
                      <div key={wave}>
                        <p className="sticky top-0 bg-surface-2 px-4 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-3">
                          Wave {wave}
                        </p>
                        <ul className="divide-y divide-line">
                          {byWave[wave].map((t) => (
                            <li key={t.device_id} className="flex items-center justify-between gap-3 px-4 py-2">
                              <div className="min-w-0">
                                <Link to={`/devices/${t.device_id}`} className="truncate text-[13px] text-ink hover:underline">
                                  {t.reg_number || t.imei}
                                </Link>
                                <p className="text-[11.5px] text-ink-3">
                                  {t.from_version || 'unknown'} → {t.to_version}
                                  {t.current_version ? ` · now ${t.current_version}` : ''}
                                  {t.error ? ` · ${t.error}` : ''}
                                </p>
                              </div>
                              <Badge tone={TARGET_TONE[t.state] || 'neutral'} size="sm" icon={null}>
                                {t.state.toLowerCase()}
                              </Badge>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          );
        }}
      </AsyncBoundary>
    </Page>
  );
}
